import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  Image as ImageIcon,
  FilmSlate,
  FileText,
  CircleNotch,
  X,
} from "@phosphor-icons/react";
import { formatMediaBytes } from "./utils";

export function MediaUploadDialog({
  isUploading,
  onUpload,
  onClose,
}: {
  isUploading: boolean;
  onUpload: (file: File, folder: string) => void;
  onClose: () => void;
}) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadFolder, setUploadFolder] = useState("profiles");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;
    onUpload(selectedFile, uploadFolder);
  };

  return (
    <Dialog open onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogHeader onClose={onClose}>
          <AdminDialogHeading
            icon={Upload}
            title="Upload fajla"
            description="Odaberite fajl i folder za upload."
          />
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <Label>Folder</Label>
              <Select value={uploadFolder} onValueChange={setUploadFolder}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="profiles">Profili</SelectItem>
                  <SelectItem value="services">Usluge</SelectItem>
                  <SelectItem value="blog">Blog</SelectItem>
                  <SelectItem value="documents">Dokumenti</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-file">Fajl</Label>
              <div className="relative">
                <input
                  id="upload-file"
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
                {selectedFile ? (
                  <div className="flex items-center gap-3 rounded-xl border px-4 py-3">
                    {selectedFile.type.startsWith("image/") ? (
                      <ImageIcon className="size-5 shrink-0 text-primary" />
                    ) : selectedFile.type.startsWith("video/") ? (
                      <FilmSlate className="size-5 shrink-0 text-primary" />
                    ) : (
                      <FileText className="size-5 shrink-0 text-primary" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatMediaBytes(selectedFile.size)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setSelectedFile(null)}
                      className="shrink-0"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <Label
                    htmlFor="upload-file"
                    className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border/60 py-10 transition-colors hover:border-primary/40 hover:bg-primary/3"
                  >
                    <Upload className="size-8 text-muted-foreground/40" />
                    <p className="mt-2 text-sm font-medium text-muted-foreground">
                      Kliknite za odabir fajla
                    </p>
                  </Label>
                )}
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" disabled={!selectedFile || isUploading}>
            {isUploading ? (
              <>
                <CircleNotch className="size-4 animate-spin" />
                Uploadovanje...
              </>
            ) : (
              <>
                <Upload className="size-4" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
